import * as React from 'react';

// API
import apibridge from '../../../apibridge';

// UTILS
import { validationErrorsToString } from '../../../utils';

// REDUX
import { useAppSelector } from '../../../store';

// PACKAGES
import { ErrorMessage, Field, FastField, FieldArray, Form, Formik } from 'formik';
import * as Yup from 'yup';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import SingleSelect from '../../../components/SingleSelect';
import Button from '../../../components/Button';
import FileUpload from '../../../components/FileUpload';
import ErrorMes from '../../../components/ErrorMessage';
import LoadingSpinner from '../../../components/LoadingSpinner';
import OrganisationLogo from '../../../components/OrganisationLogo';
import CertificationSuccess from '../ComponentForms/CertificationSuccess';
import ExistingCertification from '../../../components/ExistingCertification';
import DateDayExpiryInput from '../../../components/DateDayExpiryInput';

// ICONS
import { ReactComponent as PlusIcon } from '../../../assets/svg/plus.svg';

// TYPES
import {
	AccountExistingCertificationsCertification,
	AccountJoinOrganisationCertification,
	AccountJoinOrganisationCommand,
	DomainCertificationType,
	DomainProfileType,
	DomainRelationshipType,
	OrganisationSearchOrganisationSearchSummary,
	AccountLinkChildNameCommand,
	AccountExistingCertificationsResponse
} from '../../../api/models';

interface CertificationParentTypes {
	organisationData?: OrganisationSearchOrganisationSearchSummary;
	profileData?: { label?: string; value?: string } | undefined;
}

const CertificationParent: React.FC<CertificationParentTypes> = ({ organisationData, profileData }) => {
	// Check if User is logged in or not
	const isUserLoggedIn = useAppSelector((state) => state.user.loggedIn);

	// Get Website Url from Redux store
	const websiteUrl = useAppSelector((state) => state.lists.websiteUrl);

	// Get all states from Redux store
	const states = useAppSelector((state) => state?.lists.states);

	// Error and Loading states
	const [showError, setShowError] = React.useState<boolean>(false);

	const [formErrorMessage, setFormErrorMessage] = React.useState<string>();

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	const [success, setSuccess] = React.useState<boolean>(false);

	const [existingCertification, setExistingCertification] = React.useState<
		AccountExistingCertificationsCertification[] | undefined
	>([]);

	const getExistingCertifications = async () => {
		setIsLoading(true);

		const response: AccountExistingCertificationsResponse = await apibridge.getAccountExistingCertifications();

		if (response.isError) {
			setIsLoading(false);
		} else {
			setExistingCertification(
				response.result &&
					response.result.filter(
						(item: AccountExistingCertificationsCertification) =>
							item.certificationType === DomainCertificationType.WorkingWithChildrenCheck && item.validCertification
					)
			);

			setIsLoading(false);

			setShowError(false);
		}
	};

	React.useEffect(() => {
		getExistingCertifications();
	}, []);

	// Get all Relationship Types from Redux store
	const relationship = useAppSelector((state) => state?.lists.relationshipTypes);

	// Set initial template
	const certificationTemplate: AccountJoinOrganisationCertification = {
		certificationType: DomainCertificationType.WorkingWithChildrenCheck,
		state: '',
		expiry: '',
		certificationNumber: '',
		createAttachments: [],
		userAcknowledgement: false
	};

	const existingWWCCRegistration = existingCertification?.filter(
		(item: AccountExistingCertificationsCertification) =>
			item.certificationType === DomainCertificationType.WorkingWithChildrenCheck
	);

	// Set Initial Values
	const initalParentValues = {
		relationshipWithChild: '',
		children: [{ childsName: '' }],
		existingWWCCRegistration: existingWWCCRegistration || [],
		workingWithChildren: { ...certificationTemplate },
		volunteerStatus: '',
		userAcknowledgement: false,
		validExistingWorkingWithChildrenCheck: (existingCertification && existingCertification.length > 0) || false,
		existingWorkingWithChildren: false,
		acknowledge: false
	};

	// Validation
	const parentFormValidation = Yup.object().shape({
		relationshipWithChild: Yup.string().required('Required'),
		children: Yup.array().of(
			Yup.object().shape({
				childsName: Yup.string().required('Required')
			})
		),
		workingWithChildren: Yup.object().when('volunteerStatus', {
			is: 'workingWithChildren',
			then: Yup.object().shape({
				state: Yup.string().required('Required'),
				expiry: Yup.string().required('Required'),
				createAttachments: Yup.array().min(1, 'Required').required('Required'),
				certificationNumber: Yup.string().required('Required'),
				userAcknowledgement: Yup.boolean().oneOf([true], 'You must agree to continue').required('Required')
			})
		}),
		existingWWCCRegistration: Yup.array().when('volunteerStatus', {
			is: 'useExisting',
			then: Yup.array(),
			otherwise: Yup.array().of(
				Yup.object({
					certificationType: Yup.string().required('Required'),
					state: Yup.string().required('Required'),
					expiry: Yup.string().required('Required'),
					certificationNumber: Yup.string().required('Required'),
					createAttachments: Yup.array().min(1, 'Required').required(),
					userAcknowledgement: Yup.boolean().oneOf([true], 'You must agree to continue').required('Required')
				})
			)
		}),
		volunteerStatus: Yup.string().required('Required'),
		acknowledge: Yup.boolean().oneOf([true], 'You must agree to continue').required('Required')
	});

	// Submit Form
	const parentFormSubmit = async (values: any) => {
		setIsLoading(true);

		const data: AccountJoinOrganisationCommand = {
			organisationIdentifier: organisationData?.identifier,
			profileType: profileData?.value as DomainProfileType,
			volunteerRequest:
				values.volunteerStatus === 'workingWithChildren' || values.volunteerStatus === 'useExisting' ? true : false,
			certifications: values.workingWithChildren.certificationNumber ? [{ ...values.workingWithChildren }] : []
		};

		const response = await apibridge.doJoinOrganisation(data);

		if (response.isError) {
			setIsLoading(false);

			setShowError(true);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			values.children.map(async (child: any) => {
				const childData: AccountLinkChildNameCommand = {
					userProfileId: response.result.profileId,
					name: child.childsName,
					relationshipType: values.relationshipWithChild as DomainRelationshipType
				};

				const res = await apibridge.postAccountLinkChildName(childData);

				if (res.isError) {
					setIsLoading(false);

					setShowError(true);

					setFormErrorMessage(
						res.validationErrors.length
							? validationErrorsToString(res.validationErrors)
							: res.message || 'An error has occurred'
					);
				} else {
					setSuccess(true);

					setShowError(false);

					setIsLoading(false);
				}
			});
		}
	};

	return (
		<div className="page-add-certification">
			{showError ? (
				<ErrorMes message={formErrorMessage} />
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{!success ? (
									<>
										<div className="header row">
											<div className="section-header col-auto">
												<OrganisationLogo src={organisationData && organisationData.image} />

												<h2 className="h1">Joining {organisationData?.name} as a Parent or Guardian</h2>

												<p className="sub-grey-text">Please provide additional certification information.</p>
											</div>
										</div>

										<Formik
											initialValues={initalParentValues}
											validationSchema={parentFormValidation}
											onSubmit={parentFormSubmit}
											enableReinitialize
										>
											{(formik) => {
												return (
													<Form translate="yes">
														<div className="form-group">
															<label htmlFor="relationship">1. Relationship to child or children</label>

															<Field
																name="relationshipWithChild"
																children={() => (
																	<SingleSelect
																		onChange={(val: string) => formik.setFieldValue('relationshipWithChild', val)}
																		name="relationshipWithChild"
																		options={relationship}
																	/>
																)}
															/>

															<ErrorMessage name="relationshipWithChild">
																{(error) => <div className="form-validation-error">{error}</div>}
															</ErrorMessage>
														</div>

														<div className="form-group">
															<FieldArray name="children">
																{(props) => {
																	const { push, remove, form } = props;
																	const { values } = form;
																	const { children } = values;
																	return (
																		<>
																			{children.map((child: any, index: number) => {
																				return (
																					<div className={children.length > 1 ? 'form-group' : ''} key={index}>
																						<label className="sub-grey-text" htmlFor={`children[${index}].childsName`}>
																							Child's Name
																						</label>

																						<div className="form-two-button-group">
																							<Field
																								type="text"
																								value={formik.values.children[index].childsName || ''}
																								name={`children[${index}].childsName`}
																								onChange={(val: any) => {
																									formik.setFieldValue(
																										`children[${index}].childsName`,
																										val.target.value
																									);
																								}}
																								className="form-control"
																							/>

																							{index !== 0 ? (
																								<button
																									onClick={() => remove(index)}
																									className="btn-secondary mleft-10"
																								>
																									Remove
																								</button>
																							) : null}
																						</div>

																						<ErrorMessage name={`children[${index}].childsName`}>
																							{(error) => <div className="form-validation-error">{error}</div>}
																						</ErrorMessage>
																					</div>
																				);
																			})}

																			<button
																				onClick={() => {
																					push({ childsName: '' });
																				}}
																				type="button"
																				className="add-more-children full-width"
																			>
																				<PlusIcon className="icon" />
																				Add another child
																			</button>
																		</>
																	);
																}}
															</FieldArray>
														</div>

														<div className="form-group">
															<label htmlFor="volunteer">2. Do you intend to volunteer at this organisation?</label>

															<p className="sub-grey-text">
																You will need to provide your Working With Children Check before you will be permitted
																to volunteer at this organisation
															</p>

															{existingCertification &&
																existingCertification.find((cert) => cert.validCertification) && (
																	<label
																		className={
																			formik.values.volunteerStatus === 'useExisting'
																				? 'form-control-radio radio-selected'
																				: 'form-control-radio'
																		}
																	>
																		<Field name="volunteerStatus" type="radio" value={'useExisting'} />
																		Yes, use my existing WWCC details
																	</label>
																)}

															{formik.values.volunteerStatus === 'useExisting' ? (
																<>
																	<ExistingCertification
																		existingCertifications={existingCertification}
																		existingWWCCRegistration={existingWWCCRegistration}
																	/>
																</>
															) : null}

															<label
																className={
																	formik.values.volunteerStatus === 'workingWithChildren'
																		? 'form-control-radio radio-selected'
																		: 'form-control-radio'
																}
															>
																<Field name="volunteerStatus" type="radio" value={'workingWithChildren'} />
																Yes here are my Working With Children Check details
															</label>

															{formik.values.volunteerStatus === 'workingWithChildren' ? (
																<div className="form-section-title">
																	<div className="form-group">
																		<label className="sub-grey-text" htmlFor="state">
																			State of registration
																		</label>

																		<Field
																			children={() => (
																				<SingleSelect
																					name="workingWithChildren.state"
																					options={states}
																					onChange={(val: string) =>
																						formik.setFieldValue('workingWithChildren.state', val)
																					}
																					value={formik.values.workingWithChildren.state}
																				/>
																			)}
																		/>

																		<ErrorMessage name="workingWithChildren.state">
																			{(error) => <div className="form-validation-error">{error}</div>}
																		</ErrorMessage>
																	</div>

																	<div className="form-group two-col">
																		<div className="sub-form-group">
																			<label className="sub-grey-text" htmlFor="state">
																				WWCC Number
																			</label>

																			<Field
																				className="form-control"
																				type="text"
																				name="workingWithChildren.certificationNumber"
																				onChange={formik.handleChange}
																				value={formik.values.workingWithChildren.certificationNumber}
																			/>

																			<ErrorMessage name="workingWithChildren.certificationNumber">
																				{(error) => <div className="form-validation-error">{error}</div>}
																			</ErrorMessage>
																		</div>

																		<div className="sub-form-group">
																			<label className="sub-grey-text" htmlFor="expiry">
																				Expiry
																			</label>

																			<Field
																				className="form-control"
																				placeholder="MM / YYYY"
																				name="workingWithChildren.expiry"
																				children={() => (
																					<DateDayExpiryInput
																						dateTimeString={formik.values.workingWithChildren.expiry || ''}
																						onChange={(val: any) =>
																							formik.setFieldValue(`workingWithChildren.expiry`, val)
																						}
																						excludePast={true}
																					/>
																				)}
																			/>
																			<ErrorMessage name="workingWithChildren.expiry">
																				{(error) => <div className="form-validation-error">{error}</div>}
																			</ErrorMessage>
																		</div>
																	</div>

																	<div className="form-group">
																		<Field
																			children={() => (
																				<>
																					<FileUpload
																						label="Photo of WWCC"
																						name="workingWithChildren.createAttachments"
																						placeholder="Choose file..."
																						multiple={true}
																						onChange={(val: string[], files: any[]) => {
																							formik.setFieldValue('workingWithChildren.createAttachments', val);
																						}}
																					/>
																				</>
																			)}
																		/>{' '}
																		<ErrorMessage name={`workingWithChildren.createAttachments`}>
																			{(error) => <div className="form-validation-error">{error}</div>}
																		</ErrorMessage>
																	</div>

																	<div className="form-group acknowledge-container mbtom-0">
																		<label className="form-control-checkbox">
																			<Field
																				name="userAcknowledgement"
																				type="checkbox"
																				onChange={() =>
																					formik.setFieldValue(
																						`workingWithChildren.userAcknowledgement`,
																						!formik.values.workingWithChildren.userAcknowledgement
																					)
																				}
																				checked={formik.values.workingWithChildren.userAcknowledgement}
																			/>

																			<span>
																				I have notified the Working with Children Check authority that I am working or
																				volunteering at this organisation.
																			</span>
																		</label>

																		<ErrorMessage name={`workingWithChildren.userAcknowledgement`}>
																			{(error) => <div className="form-validation-error">{error}</div>}
																		</ErrorMessage>
																	</div>
																</div>
															) : null}

															<label
																className={
																	formik.values.volunteerStatus === 'None'
																		? 'form-control-radio radio-selected'
																		: 'form-control-radio'
																}
															>
																<Field name="volunteerStatus" type="radio" value={'None'} />
																No I do not plan on volunteering at this organisation
															</label>

															<ErrorMessage name="volunteerStatus">
																{(error) => <div className="form-validation-error">{error}</div>}
															</ErrorMessage>
														</div>

														<div className="general-divider"></div>

														<div className="form-group acknowledge-container">
															<label className="form-control-checkbox">
																<Field name="acknowledge" type="checkbox" onChange={formik.handleChange} />

																<span>
																	I acknowledge that all organisations that I choose to join on ChildAware will be able
																	to view (a) my profile information (including any documents that I have uploaded) (b)
																	the names of the orgnisations where I work or volunteer with children; and (c) my
																	roles at these organisations. The Individual User{' '}
																	<a href={`${websiteUrl}/terms-conditions-individual-user`} target="_blank">
																		Terms and Conditions
																	</a>{' '}
																	and{' '}
																	<a href={`${websiteUrl}/privacy-policy`} target="_blank">
																		Privacy Policy
																	</a>{' '}
																	continue to apply.
																</span>
															</label>

															<ErrorMessage name="acknowledge">
																{(error) => <div className="form-validation-error">{error}</div>}
															</ErrorMessage>
														</div>

														<Button type="submit" className="btn-primary full-width">
															{isUserLoggedIn ? 'Request to join' : 'Create Account'}
														</Button>

														{!!formErrorMessage && <div className="form-validation-error">{formErrorMessage}</div>}
													</Form>
												);
											}}
										</Formik>
									</>
								) : (
									<CertificationSuccess organisationName={organisationData && organisationData.name} />
								)}
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default CertificationParent;
